/**
 * The curated catalogue: the common products vendors quick-add instead of typing.
 *
 * Vendor listings LINK to these rows rather than copying them, so an edit here is not a
 * local change — it reaches every vendor selling the product and every shopper looking at
 * it. That fact is what the whole screen is arranged around: the reach of a row is a
 * column, not something you discover after opening one, and retiring is offered instead of
 * deletion so existing listings keep working.
 *
 * The other thing it now says out loud is what each master charges, and which rung of the
 * fee chain decided that — because a master with no fee of its own, in a category with no
 * rule, earns the platform nothing, and nothing on the old screen would have told you.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Boxes,
  Copy,
  ImageOff,
  Images,
  MoreHorizontal,
  Percent,
  PillBottle,
  Plus,
  Store,
  Undo2,
  Users,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ago } from '@/lib/format';
import { export_catalog_products } from '@/features/catalog-products/api';
import {
  use_catalog_counts,
  use_catalog_products,
  use_update_catalog_product,
} from '@/features/catalog-products/hooks';
import {
  CATALOG_SORT_FIELDS,
  FEE_SOURCE_LABEL,
  FEE_SOURCE_TONE,
  VERTICAL_LABEL,
  fee_amount,
  photo_count,
  type CatalogCounts,
  type CatalogProduct,
  type CatalogQuery,
  type Vertical,
} from '@/features/catalog-products/types';
import { use_product_categories } from '@/features/product-categories/hooks';
import { category_label, shared_category_options } from '@/features/product-categories/types';

const SEGMENTS = ['all', 'unused', 'no_photos', 'own_fee', 'retired'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<CatalogQuery>> = {
  all: { is_active: 'true' },
  // Curation nobody took up: either a product to promote, or one to retire.
  unused: { is_active: 'true', unused: 'true' },
  // Every vendor listing linked to one of these shows an empty image slot to shoppers.
  no_photos: { is_active: 'true', no_photos: 'true' },
  own_fee: { has_own_fee: 'true' },
  retired: { is_active: 'false' },
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  all: {
    label: 'Live',
    title: 'Master catalogue',
    blurb: 'The curated products every vendor can list from',
  },
  unused: {
    label: 'Nobody sells it',
    title: 'Curated but unsold',
    blurb: 'No vendor has ever linked to these — promote them or retire them',
  },
  no_photos: {
    label: 'No photo',
    title: 'Masters with no picture',
    blurb: 'Every vendor listing linked to one of these shows a shopper an empty slot',
  },
  own_fee: {
    label: 'Own fee',
    title: 'Masters that set their own fee',
    blurb: 'These override their category default for every vendor selling them',
  },
  retired: {
    label: 'Retired',
    title: 'Retired from the catalogue',
    blurb: 'Existing listings keep working; no new one can link to these',
  },
};

export function CatalogProductPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'all';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [vertical, set_vertical] = use_url_state(
    'vertical',
    enum_param(['FOOD', 'PHARMACY'] as const),
  );
  const [category, set_category] = use_url_state('category', string_param);
  const [unused, set_unused] = use_url_state('unused', bool_param);
  const [no_photos, set_no_photos] = use_url_state('no_photos', bool_param);
  const [has_own_fee, set_has_own_fee] = use_url_state('has_own_fee', bool_param);
  const [prescription, set_prescription] = use_url_state('prescription', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    sort_param(CATALOG_SORT_FIELDS, { by: 'name', dir: 'asc' }),
  );

  const reset_page = () => set_page(1);
  const update_m = use_update_catalog_product();

  const filters: CatalogQuery = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      vertical: vertical || undefined,
      category: category || undefined,
      unused: unused || SEGMENT_FILTERS[segment].unused,
      no_photos: no_photos || SEGMENT_FILTERS[segment].no_photos,
      has_own_fee: has_own_fee || SEGMENT_FILTERS[segment].has_own_fee,
      prescription: prescription || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [segment, page, page_size, q, vertical, category, unused, no_photos, has_own_fee, prescription, from, to],
  );

  const { data, isLoading, isFetching, error, refetch } = use_catalog_products(filters, sort);
  const { data: counts } = use_catalog_counts();
  const { data: all_categories } = use_product_categories();
  const category_options = useMemo(
    () => shared_category_options(all_categories),
    [all_categories],
  );

  async function set_active(p: CatalogProduct, is_active: boolean) {
    try {
      await update_m.mutateAsync({ id: p.id, input: { is_active } });
      notify.success(is_active ? 'Back in the catalogue' : 'Retired from the catalogue', {
        description: is_active
          ? `${p.name} can be linked to again.`
          : `${p.name} stays on its ${p.vendor_count} existing listing${p.vendor_count === 1 ? '' : 's'}, but no new one can link to it.`,
      });
    } catch (err) {
      notify.error(err, 'Could not change this product.');
    }
  }

  /* --------------------------------- chips --------------------------------- */

  const bool_chip = (
    key: string,
    label: string,
    value: string,
    yes: string,
    no: string,
    clear: () => void,
  ) =>
    value
      ? {
          key,
          label,
          value: value === 'true' ? yes : no,
          advanced: true,
          on_clear: () => {
            clear();
            reset_page();
          },
        }
      : null;

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    vertical && {
      key: 'vertical',
      label: 'Kind',
      value: VERTICAL_LABEL[vertical as Vertical],
      on_clear: () => set_vertical(''),
    },
    category && {
      key: 'category',
      label: 'Category',
      value: category_options.find((o) => o.value === category)?.label ?? category,
      on_clear: () => set_category(''),
    },
    segment === 'unused'
      ? null
      : bool_chip('unused', 'Vendors', unused, 'None yet', 'At least one', () => set_unused('')),
    segment === 'no_photos'
      ? null
      : bool_chip('no_photos', 'Photos', no_photos, 'None', 'Has some', () => set_no_photos('')),
    segment === 'own_fee'
      ? null
      : bool_chip('has_own_fee', 'Fee', has_own_fee, 'Set here', 'Inherited', () =>
          set_has_own_fee(''),
        ),
    bool_chip('prescription', 'Prescription', prescription, 'Required', 'Not required', () =>
      set_prescription(''),
    ),
    (from || to) && {
      key: 'date',
      label: 'Changed',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_vertical('');
    set_category('');
    set_unused('');
    set_no_photos('');
    set_has_own_fee('');
    set_prescription('');
    set_from('');
    set_to('');
    reset_page();
  }

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<CatalogProduct>[]>(
    () => [
      {
        id: 'name',
        header: 'Product',
        meta: { sort_key: 'name', label: 'Product', width: '20rem', csv: (r) => r.name },
        cell: ({ row }) => {
          const p = row.original;
          const photos = photo_count(p);
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              {p.images[0] ? (
                <AuthImage
                  path={`${p.images[0].path}?variant=thumb`}
                  alt={p.name}
                  className="h-9 w-9 shrink-0 overflow-hidden rounded-control object-cover"
                  data-testid={`catalog-image-${p.id}`}
                />
              ) : (
                /*
                  Not a neutral placeholder. A master with no photo hands an empty slot to
                  every vendor listing it and every shopper who scrolls past, so the row
                  should look like something is missing.
                */
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-warning/12 text-warning"
                  title="No curated photo"
                  data-testid={`catalog-no-image-${p.id}`}
                >
                  <ImageOff className="h-4 w-4" />
                </span>
              )}
              <span className="flex min-w-0 flex-col">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-semibold" data-testid={`catalog-name-${p.id}`}>
                    {p.name}
                  </span>
                  {p.is_prescription_required && (
                    <Badge tone="destructive" size="sm" data-testid={`catalog-rx-${p.id}`}>
                      <PillBottle className="h-3 w-3" /> Rx
                    </Badge>
                  )}
                </span>
                <span className="truncate text-micro text-muted-foreground">
                  {[p.brand, p.pack_size, photos === 0 ? 'no photo' : `${photos} photo${photos === 1 ? '' : 's'}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'category',
        header: 'Category',
        meta: { sort_key: 'category', label: 'Category', csv: (r) => r.category },
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium">
              {category_label(all_categories, row.original.vertical, row.original.category)}
            </span>
            <span className="truncate text-micro text-muted-foreground">
              {VERTICAL_LABEL[row.original.vertical]}
            </span>
          </span>
        ),
      },
      {
        id: 'vendor_count',
        header: 'Vendors',
        // Not sortable: it is an aggregate across two other tables, and offering a sort
        // the server cannot honour is worse than offering none.
        enableSorting: false,
        meta: { label: 'Vendors', align: 'right', csv: (r) => r.vendor_count },
        cell: ({ row }) => {
          const n = row.original.vendor_count;
          return n === 0 ? (
            <span className="text-micro text-muted-foreground" data-testid={`vendors-${row.original.id}`}>
              Nobody yet
            </span>
          ) : (
            <span className="flex flex-col items-end" data-testid={`vendors-${row.original.id}`}>
              <span className="font-semibold tabular">{n}</span>
              <span className="text-micro text-muted-foreground">
                {n === 1 ? 'shop sells it' : 'shops sell it'}
              </span>
            </span>
          );
        },
      },
      {
        id: 'fee',
        header: 'Fee',
        enableSorting: false,
        meta: {
          label: 'Fee',
          align: 'right',
          csv: (r) => `${fee_amount(r.effective)} (${r.effective.source})`,
        },
        cell: ({ row }) => {
          const p = row.original;
          /*
           * Both halves of the fact. "10%" alone does not say whether changing the Dry Food
           * rule would move this product or leave it untouched, and that is the question an
           * admin is actually asking when they look at this column.
           */
          return (
            <span className="flex flex-col items-end gap-0.5">
              <span className="font-semibold tabular" data-testid={`fee-${p.id}`}>
                {fee_amount(p.effective)}
              </span>
              <Badge tone={FEE_SOURCE_TONE[p.effective.source]} size="sm">
                {p.effective.source === 'CATEGORY'
                  ? `${category_label(all_categories, p.vertical, p.category)} default`
                  : FEE_SOURCE_LABEL[p.effective.source]}
              </Badge>
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        meta: { label: 'Status', csv: (r) => (r.is_active ? 'Live' : 'Retired') },
        cell: ({ row }) => (
          <Badge
            tone={row.original.is_active ? 'success' : 'neutral'}
            size="sm"
            dot
            data-testid={`status-${row.original.id}`}
          >
            {row.original.is_active ? 'Live' : 'Retired'}
          </Badge>
        ),
      },
      {
        id: 'updated_at',
        header: 'Changed',
        meta: { sort_key: 'updated_at', label: 'Changed', csv: (r) => r.updated_at },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-data text-muted-foreground">
            {ago(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right', width: '1%' },
        cell: ({ row }) => {
          const p = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Catalogue actions"
                  data-testid={`row-actions-${p.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(p.id);
                    notify.success('Catalogue ID copied');
                  }}
                  testid={`copy-${p.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy catalogue ID
                </RowAction>
                {p.vendor_count > 0 && (
                  <RowAction
                    on_select={() =>
                      window.open(
                        `/product-approvals?segment=live&q=${encodeURIComponent(p.name)}`,
                        '_self',
                      )
                    }
                    testid={`listings-${p.id}`}
                  >
                    <Store className="h-4 w-4" /> See the vendor listings
                  </RowAction>
                )}
                <DropdownMenuSeparator />
                {/* Retire rather than delete: listings already linked keep working. */}
                <RowAction
                  on_select={() => void set_active(p, !p.is_active)}
                  testid={`toggle-active-${p.id}`}
                >
                  <Undo2 className="h-4 w-4" />
                  {p.is_active ? 'Retire from the catalogue' : 'Bring it back'}
                </RowAction>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all_categories],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={
        <div className="flex items-center gap-2">
          <SavedViews module={`catalogue:${segment}`} is_filtered={chips.some(Boolean)} />
          <Button asChild size="sm">
            <Link to="/catalogue/new" data-testid="catalog-new">
              <Plus className="mr-1 h-4 w-4" /> New product
            </Link>
          </Button>
        </div>
      }
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'unused'
                ? counts?.unused
                : v === 'no_photos'
                  ? counts?.no_photos
                  : v === 'own_fee'
                    ? counts?.own_fee
                    : v === 'retired'
                      ? counts?.retired
                      : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<CatalogProduct>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(p) => p.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(p) => `/catalogue/${p.id}`}
        storage_key={`catalogue:${segment}`}
        export_name="catalogue"
        on_export_all={() => export_catalog_products(filters, sort)}
        caption="Curated master products"
        test_id="catalogue-list"
        empty={{
          icon: Boxes,
          title: chips.some(Boolean)
            ? 'No products match these filters'
            : segment === 'all'
              ? 'The catalogue is empty'
              : `Nothing in ${copy.label.toLowerCase()}`,
          description:
            segment === 'all'
              ? 'Add the products your vendors sell most, so they pick rather than type.'
              : 'Nothing here — which in this view is the good outcome.',
          action:
            segment === 'all' ? (
              <Button asChild size="sm">
                <Link to="/catalogue/new">
                  <Plus className="mr-1 h-4 w-4" /> New product
                </Link>
              </Button>
            ) : undefined,
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <BoolFilter
                  label="Vendors"
                  value={segment === 'unused' ? 'true' : unused}
                  on_change={(v) => {
                    set_unused(v);
                    reset_page();
                  }}
                  true_label="None yet"
                  false_label="At least one"
                  test_id="filter-unused"
                />
                <BoolFilter
                  label="Photos"
                  value={segment === 'no_photos' ? 'true' : no_photos}
                  on_change={(v) => {
                    set_no_photos(v);
                    reset_page();
                  }}
                  true_label="None"
                  false_label="Has some"
                  test_id="filter-no-photos"
                />
                <BoolFilter
                  label="Fee"
                  value={segment === 'own_fee' ? 'true' : has_own_fee}
                  on_change={(v) => {
                    set_has_own_fee(v);
                    reset_page();
                  }}
                  true_label="Set here"
                  false_label="Inherited"
                  test_id="filter-own-fee"
                />
                <BoolFilter
                  label="Prescription"
                  value={prescription}
                  on_change={(v) => {
                    set_prescription(v);
                    reset_page();
                  }}
                  true_label="Required"
                  false_label="Not required"
                  test_id="filter-prescription"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Product or brand…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Kind"
              value={vertical}
              on_change={(v) => {
                set_vertical(v as Vertical);
                reset_page();
              }}
              options={[
                { value: 'FOOD', label: VERTICAL_LABEL.FOOD },
                { value: 'PHARMACY', label: VERTICAL_LABEL.PHARMACY },
              ]}
              all_label="Both"
              test_id="filter-vertical"
            />
            <SelectFilter
              label="Category"
              value={category}
              on_change={(v) => {
                set_category(v);
                reset_page();
              }}
              options={category_options}
              all_label="Any category"
              test_id="filter-category"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Changed"
            />
          </FilterBar>
        }
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: CatalogCounts | undefined): Metric[] {
  return [
    {
      key: 'live',
      label: 'Curated products',
      value: counts?.live?.toLocaleString('en-IN'),
      hint: counts?.retired ? `${counts.retired} retired` : 'None retired',
      to: '/catalogue?segment=all',
      icon: Boxes,
      loading: !counts,
    },
    {
      key: 'linked',
      label: 'Listings using them',
      // The reach of the whole catalogue: how much of the shop is curated rather than
      // typed by a vendor. It is the argument for maintaining this screen at all.
      value: counts?.linked_listings?.toLocaleString('en-IN'),
      hint: 'Vendor listings linked',
      to: '/product-approvals?segment=live&linked=true',
      icon: Users,
      loading: !counts,
    },
    {
      key: 'unused',
      label: 'Nobody sells it',
      value: counts?.unused?.toLocaleString('en-IN'),
      hint: 'Curated, never picked up',
      tone: counts && counts.unused > 0 ? 'warning' : 'default',
      to: '/catalogue?segment=unused',
      icon: Store,
      loading: !counts,
    },
    {
      key: 'no_photos',
      label: 'No photo',
      // Not cosmetic: a master with no picture hands an empty slot to every vendor
      // listing it and every shopper who scrolls past one.
      value: counts?.no_photos?.toLocaleString('en-IN'),
      hint: 'Shoppers see an empty slot',
      tone: counts && counts.no_photos > 0 ? 'warning' : 'default',
      to: '/catalogue?segment=no_photos',
      icon: Images,
      loading: !counts,
    },
    {
      key: 'no_fee',
      label: 'Charging nothing',
      // No fee of its own AND no rule on its category — the platform earns nothing on
      // every sale of these, and no screen said so before.
      value: counts?.no_fee?.toLocaleString('en-IN'),
      hint: 'No fee, and no category rule',
      tone: counts && counts.no_fee > 0 ? 'destructive' : 'default',
      to: '/platform-fees',
      icon: Percent,
      loading: !counts,
    },
  ];
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}
